import _ from 'underscore';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {
  FolderWithPermissions,
  MinimalFolder,
  FolderSummary,
  FolderListingExtras,
  Type,
  FolderType,
} from 'constants/FolderConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import {modelInaccessible} from 'models/ManagedModelsReduxActions';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as net from 'util/Net';
import sanitizeFetchParams, {SanitizedFetchParams, FetchParams} from 'util/sanitizeFetchParams';

import FolderDAOCache from './FolderDAOCache';
interface FetchAllParams {
  type?: FolderType;
  level?: IntentLevel;
}

interface FolderWithPermissionsById {
  [folderId: string]: FolderWithPermissions;
}

export interface CreateFolderParams {
  parent: string | null;
  name: string;
  description?: string;
  type: FolderType;
}

interface BulkFetchFolderListingExtrasResponse {
  folderExtrasByFolderId: {
    [apiIdentifier: string]: FolderListingExtras;
  };
  foldersById: {[id: string]: FolderWithPermissions};
}

function sanitizeFolderFetchParams(fetchParams: FetchParams = {}): SanitizedFetchParams {
  return sanitizeFetchParams(
    fetchParams,
    'batchSchema',
    'collection',
    'containerSchema',
    'excludeItemsInFolder',
    'filter',
    'q',
    'refetch',
    'section',
    'sort',
    'summary',
    'tagSchema'
  );
}

function fetch(id: string, params: FetchParams): JQueryPromise<FolderSummary> {
  const {refetch, clear} = params;
  // Sanitize arguments...
  const sanitizedParams = sanitizeFolderFetchParams(params);
  // ... and then call through to cache
  return FolderDAOCache.cachedFetchSummary(
    id,
    {summary: sanitizedParams.summary!, offset: sanitizedParams.offset!},
    // @ts-expect-error
    {refetch, clear}
  )!;
}

function fetchWithPermission(id: string, params: FetchParams): JQueryPromise<FolderWithPermissions> {
  const {refetch, clear} = params;
  // Sanitize arguments...
  const sanitizedParams = sanitizeFolderFetchParams(params);
  // ... and then call through to cache
  return FolderDAOCache.cachedFetchWithPermissions(
    id,
    {offset: sanitizedParams.offset!},
    // @ts-expect-error
    {refetch, clear}
  );
}

export default {
  fetchAll(param?: FetchAllParams): JQueryPromise<Array<MinimalFolder>> {
    // Sanitize arguments...
    if (param == null) {
      param = {};
    }
    let {type, level} = param;
    if (type == null) {
      type = Type.ALL;
    }
    if (level == null) {
      level = 'read';
    }
    // ... and then call through to cache
    return FolderDAOCache.cachedFetchAll(type, level);
  },

  fetch,

  fetchWithPermission,

  async bulkFetch(
    this: void,
    folderIds: Array<string>,
    params: FetchParams = {}
  ): Promise<{[id: string]: FolderWithPermissions}> {
    const ModelType = require('models/ManagedModelDefinitions').ManagedModelType;
    const foldersById: FolderWithPermissionsById = {};
    for (const folderId of folderIds) {
      // TODO: move inaccesible error handling out of DAO into higher levels
      // Once that's done, uninline require in notifications.tsx
      try {
        const folder = await fetchWithPermission(folderId, params);
        if (folder != null) {
          foldersById[folderId] = folder;
        }
      } catch (e) {
        if (net.isServerRequestError(e) && e.responseJSON && isInaccessibleInfo(e.responseJSON)) {
          getStore().dispatch(modelInaccessible(ModelType.FOLDER_BY_ID, {[folderId]: e.responseJSON}));
        } else {
          throw e;
        }
      }
    }
    return Promise.resolve(foldersById);
  },

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  update(id: string, data: any, {parentFolderId}: {parentFolderId: string}): Promise<FolderSummary> {
    return net
      .patchJSONPromise({
        url: `/1/api/folders/${id}`,
        data,
      })
      .then((folder) => {
        // eslint-disable-next-line no-restricted-syntax
        const parentFolderIds = _.uniq(_.compact([data.parentId, parentFolderId]));
        for (const folderId of Array.from(parentFolderIds)) {
          FolderDAOCache.invalidateFolderContents(folderId);
        }
        FolderDAOCache.invalidateFolder(id);
        return folder;
      });
  },

  create({parent, name, description, type}: CreateFolderParams): JQueryPromise<MinimalFolder> {
    if (description == null) {
      description = '';
    }
    const xhr = net
      .postJSON({
        url: '/1/api/folders/',
        data: {
          parent,
          name,
          description,
          type,
        },
      })
      .done(() => {
        FolderDAOCache.invalidateFolder(parent);
      });
    return xhr;
  },

  registerCreatedFolder(folder: MinimalFolder, callback?: (folder: MinimalFolder) => void): void {
    notifications.success(`Successfully created folder "${folder.name}"`);

    if (callback != null) {
      callback(folder);
    }
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  listFiles(folderId: string, options: FetchParams) {
    const sanitizedParams = sanitizeFolderFetchParams(options);
    return net.getJSON(`/1/api/folders/${folderId}/files/`, sanitizedParams);
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getNestedFiles(fileAndFolderIds: Array<string>, options: FetchParams) {
    const sanitizedParams = Object.assign(sanitizeFolderFetchParams(options), {ids: fileAndFolderIds});
    return net.getJSON('/1/api/folders/actions/get-nested-files', sanitizedParams);
  },

  bulkFetchFolderListingExtras(folderIds: Array<string>): Promise<BulkFetchFolderListingExtrasResponse> {
    return net.postJSONPromise({
      url: '/1/api/folders:bulk-get-listing-extras',
      data: {folderIds},
    });
  },

  getNestedDnaSequencesAndOligos(folderIds: Array<string>): Promise<{sequenceIds: Array<string>}> {
    return net.postJSONPromise({
      url: '/1/api/folders:get-nested-dna-sequences-and-oligos',
      data: {folderIds},
    });
  },

  ungroupFolder(folderId: string): Promise<void> {
    return net.postJSONPromise({url: `/1/api/folders/${folderId}/ungroup`});
  },
};
